<template>
  <v-app>
    <LoadingScreen v-if="authStore.globalLoading" />

    <router-view v-else />
    <div
      data-cy="offline-banner"
      v-if="!axiosStore.online"
      style="
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100vw;
        height: 20px;
        background: orange;
        text-align: center;
        justify-content: center;
        z-index: 1000;
      ">
      Offline
    </div>

    <div
      data-cy="offline-banner"
      v-if="spaceStore.readOnly"
      style="
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100vw;
        height: 20px;
        background: orange;
        text-align: center;
        justify-content: center;
        z-index: 1000;
      ">
      {{ $t("other.readOnly") }}
    </div>
    <div
      data-cy="offline-banner"
      v-if="!spaceStore.readOnly && noLicence"
      style="
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100vw;
        height: 20px;
        background: orange;
        text-align: center;
        justify-content: center;
        z-index: 1000;
      ">
      {{
        $t("other.missingLicence", {
          level: spaceStore.getElement(route.params.nodeId as string).level
        })
      }}
    </div>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";
import { useLocale } from "vuetify";
import { useTranslation } from "i18next-vue";

// Layout Components
import TopNavBar from "./components/layout/TopNavBar.vue";
import SideBar from "./components/layout/SideBar.vue";

import LoadingScreen from "./components/layout/LoadingScreen.vue";

import allowedChildren from "./data/raw/allowedChildren.json";

import { useAuthStore } from "./stores/authStore";
import { useSpaceStore } from "./stores/spaceStore";
import { useTreeStore } from "./stores/treeStore";
import { useMainStore } from "./stores/mainStore";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { useAxiosStore } from "./stores/axiosStore";

const spaceStore = useSpaceStore();
const treeStore = useTreeStore();
const mainStore = useMainStore();
const axiosStore = useAxiosStore();
const route = useRoute();
const router = useRouter();

const createSnackbar = useSnackbar();

const routeNodeId = computed(() => {
  return (route.params.nodeId ?? route.params.parentId) as string;
});

const authStore = useAuthStore();

const { current } = useLocale();
const { i18next } = useTranslation();

function breadcrumbNavigation(route) {
  router.push(route);
}

const noLicence = computed(() => {
  const element = spaceStore.getElement(route.params.nodeId as string);

  if (!element) return false;

  if (!element.level) return false;

  const levelToLicence = {
    1: 1,
    2: 2,
    3: 4,
    undefined: 0
  };

  return !(spaceStore.currentSpace.licence & levelToLicence[element.level]);
});

const { t } = useTranslation();
const createConfirm = useConfirm();

async function copyLocation() {
  if (
    mainStore.copyData.spaceId !== mainStore.activeSpaceId &&
    (mainStore.copyData.id.endsWith("_part") ||
      mainStore.copyData.id.endsWith("_folder")) &&
    mainStore.copyData.locations.length
  ) {
    return createConfirm({
      dialogProps: { width: 400 },
      cardProps: { color: "green" },
      title: t(`actions.confirmation.copy-location.title`),
      content: t(`actions.confirmation.copy-location.content`),
      confirmationText: t(`actions.confirmation.copy-location.confirm`),
      cancellationText: t(`actions.confirmation.copy-location.cancel`),
      confirmationButtonProps: {}
    }).then((confirm) => {
      return !!confirm;
    });
  } else {
    return Promise.resolve(false);
  }
}

// Sync language between i18next and vuetify
onMounted(() => {
  console.log(
    "App mounted",
    document.cookie,
    decodeURIComponent(document.cookie).includes("verificationCookie=exists")
  );

  if (document.cookie.includes("verificationCookie=exists")) {
    // TODO Fullside Loading screen
    authStore.validateToken().finally(() => {
      if (document.cookie.includes("impersonate=true")) {
        authStore.user.impersonating = true;
      }

      console.log("Laoding complete");
    });
  } else {
  }
  current.value = i18next.language;

  document.addEventListener("copy", (evt) => {
    let nodeId: string | undefined = route.params.nodeId as string | undefined;
    if (nodeId) {
      const id = nodeId
        .replace("variant", "part")
        .replace("manufacturingStep", "part");

      spaceStore.prepareClone(id);
    }
  });

  document.addEventListener("paste", async (evt) => {
    console.log("copyData", JSON.stringify(mainStore.copyData));
    if (!mainStore.copyData.id) {
      // nothing to paste
      return;
    }

    const [, copyType] = mainStore.copyData.id.split("_");

    let nodeId: string | undefined = route.params.nodeId as string | undefined;

    const location = await copyLocation();
    if (nodeId) {
      const [, type] = nodeId.split("_");

      if (copyType === "folder") {
        spaceStore.cloneOnServer(undefined, location);
      } else if (allowedChildren[type]?.includes(copyType)) {
        if (type === copyType && copyType === "part") {
          spaceStore.cloneOnServer(nodeId, location, true);
        } else {
          nodeId = nodeId.replace("manufacturingStep", "part");
          spaceStore.cloneOnServer(nodeId, location);
        }
        // mainStore.copyData.id = null;
      } else {
        createSnackbar({
          text: "Element kann hier nicht eingefügt werden",
          showCloseButton: false,
          snackbarProps: { color: "error", timeout: 2000 }
        });
      }
    } else if (copyType === "folder") {
      spaceStore.cloneOnServer(undefined, location);
    }
  });
});
</script>
